import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { site } from "@/mocks/site";
import { testimonials } from "@/mocks/testimonials";

const BASE = "https://kdove.cn";

const principles = [
  {
    icon: "ri-eye-line",
    title: "只围观，不定罪",
    text: "我们描述画面里真实发生的动作和时间顺序，不替当事人补内心戏，也不给任何选手贴标签。",
  },
  {
    icon: "ri-links-line",
    title: "保留完整语境",
    text: "拒绝把一句话剪出来当标题。能放出前因后果的，就一定放出来，哪怕这样会让标题没那么抓眼。",
  },
  {
    icon: "ri-shield-user-line",
    title: "不碰私人信息",
    text: "涉及家庭、感情、未公开经历的素材，即使在现场被拍到，也不会出现在这里。",
  },
  {
    icon: "ri-refresh-line",
    title: "错了就改",
    text: "观众指出的错误我们会在 48 小时内核对并更新，修改记录会留在文章底部。",
  },
];

const milestones = [
  { year: "2024", text: "从一场走秀事故的复盘笔记开始，慢慢变成固定更新的现场整理栏目。" },
  { year: "2025", text: "建立栏目分类与时间线机制，把零散片段串成可回看的完整脉络。" },
  { year: "2026", text: "开放现场投稿渠道，内容开始来自更多第一视角的观众记录。" },
];

export default function AboutPage() {
  useDocumentMeta({
    title: "关于我们｜吃瓜前排的内容原则与定位",
    description:
      "吃瓜前排是一个专注选美吃瓜现场的娱乐内容站。这里介绍我们的网站定位、内容原则、发展理念与联系方式，围观名场面也守住分寸。",
    keywords: "选美吃瓜,关于吃瓜前排,选美吃瓜现场,内容原则",
    canonical: `${BASE}/about`,
  });

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于吃瓜前排",
      url: `${BASE}/about`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.brand,
      url: `${BASE}/`,
      email: site.email,
      slogan: site.slogan,
    },
  ];

  return (
    <>
      <JsonLd data={structuredData} />

      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20editorial%20wide%20shot%20of%20a%20theater%20audience%20seats%20facing%20a%20glowing%20stage%20with%20amber%20spotlights%2C%20soft%20bokeh%20lights%2C%20cinematic%20warm%20tones%2C%20high%20detail&width=1600&height=900&seq=about-hero-01&orientation=landscape"
            alt="剧场观众席与舞台暖光氛围"
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/72 to-foreground-950/45" />
        </div>
        <div className="relative mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <div className="[&_a]:text-background-300 [&_a:hover]:text-background-50 [&_span]:text-background-400">
              <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            </div>
            <h1 className="mt-5 font-heading text-3xl font-bold text-background-50 md:text-5xl">
              我们坐在前排，只负责把现场讲清楚
            </h1>
            <p className="mt-5 text-sm leading-relaxed text-background-200 md:text-base">
              {site.brand}关注选美比赛里那些计划之外的瞬间。翻车、尴尬、即兴、沉默——这些片段之所以好看，是因为它们无法被编排，也正因为如此，更需要有人把语境完整地留下来。
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-7">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.28em] text-primary-600">
                <span className="inline-block h-px w-6 bg-primary-400" />
                Who We Are
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                品牌介绍
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
                <p>
                  {site.brand}是一个专注选美赛事现场的娱乐内容站。我们不报道赛程结果，也不做选手排名，只把镜头对准那些"和计划不一样"的瞬间：主持人念错的名字、崴脚之后的临场反应、评委席上没端住的表情。
                </p>
                <p>
                  网站名来自一个很朴素的场景：剧场里最好的位置永远是前排，看得清、听得清，也最容易先笑出来。我们想做的就是把观众席前排的位置，让给每一个打开网页的人。
                </p>
                <p>
                  目前内容由一支小团队与外部投稿者共同维护，覆盖赛事回放整理、现场观众记录和赛后公开采访三条来源。所有素材在发布前都会经过一次语境核对。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 lg:col-span-5">
              <div className="col-span-2 rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  网站定位
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  面向喜欢围观选美花边与现场名场面的泛娱乐读者，提供有语境、可回看、不煽动的现场整理内容。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-primary-950 p-6">
                <span className="font-heading text-3xl font-bold text-accent-400">120+</span>
                <p className="mt-2 text-xs text-background-300">已整理现场片段</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-secondary-100 p-6">
                <span className="font-heading text-3xl font-bold text-secondary-900">5</span>
                <p className="mt-2 text-xs text-secondary-800">固定内容栏目</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.28em] text-primary-600">
            <span className="inline-block h-px w-6 bg-primary-400" />
            Philosophy
          </p>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            发展理念
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {milestones.map((item) => (
              <div
                key={item.year}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="font-heading text-2xl font-bold text-primary-600">
                  {item.year}
                </span>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="principle" className="scroll-mt-24 bg-background-50 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.28em] text-primary-600">
            <span className="inline-block h-px w-6 bg-primary-400" />
            Principles
          </p>
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            内容原则
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            吃瓜的乐趣不该建立在别人的难堪上。以下四条是我们选材和写作时不会让步的底线。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary-950 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            用户口碑
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="rounded-lg border border-secondary-800 bg-secondary-900 p-6"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                  <i className="ri-double-quotes-l text-lg" />
                </span>
                <blockquote className="mt-4 text-sm leading-relaxed text-background-200">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 border-t border-secondary-800 pt-4 text-xs text-background-400">
                  {item.name} · {item.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="scroll-mt-24 bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.28em] text-primary-600">
                <span className="inline-block h-px w-6 bg-primary-400" />
                Contact
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                有现场素材想投稿？发现内容有误想指正？或者只是想聊聊某场比赛？
                下面两个渠道都能找到我们，邮件一般会在两个工作日内回复。
              </p>
              <div className="mt-6 space-y-3">
                <a
                  href={`mailto:${site.email}`}
                  rel="nofollow"
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">投稿与纠错邮箱</span>
                    <span className="block text-sm font-medium text-foreground-950">
                      {site.email}
                    </span>
                  </span>
                </a>
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-wechat-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">微信公众号</span>
                    <span className="block text-sm font-medium text-foreground-950">
                      {site.wechat}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                <h3 className="font-heading text-lg font-bold text-foreground-950">
                  想第一时间收到新内容？
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  订阅吃瓜情报，每个赛事周我们会把新整理的现场片段与时间线更新发给你。
                </p>
                <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <span className="inline-flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-check-line" />
                    </span>
                    新内容上线提醒
                  </li>
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <span className="inline-flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-check-line" />
                    </span>
                    完整现场时间线
                  </li>
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <span className="inline-flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-check-line" />
                    </span>
                    不做任何广告推送
                  </li>
                  <li className="flex items-center gap-2 text-sm text-foreground-700">
                    <span className="inline-flex h-4 w-4 items-center justify-center text-primary-600">
                      <i className="ri-check-line" />
                    </span>
                    随时一键退订
                  </li>
                </ul>
                <Link
                  to="/#newsletter"
                  className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <span className="inline-flex h-4 w-4 items-center justify-center">
                    <i className="ri-mail-send-line" />
                  </span>
                  去订阅吃瓜情报
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}